import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { MailCheck } from "lucide-react";
import { forgotPassword } from "@teamblob/api-client";
import {
  AuthField,
  AuthErrorBanner,
} from "../components/desktop/auth-primitives.js";
import { Button } from "@teamblob/ui/components";

/**
 * `/forgot-password` — request a password-reset link.
 *
 * Public, standalone (not inside DesktopShell). The user enters their email;
 * the API always returns 200 (anti-enumeration), so on success we show a
 * neutral "check your inbox" state regardless of whether the email exists.
 */
export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setFormError("Enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      await forgotPassword({ email: trimmed });
      setSent(true);
    } catch {
      // The API always 200s (anti-enumeration); only a network error lands here.
      setFormError("Something went wrong. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 text-center shadow-[var(--shadow-window)]">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[var(--color-accent-amber-soft,#fdf0d5)] text-[var(--color-accent-amber-dark)]">
            <MailCheck size={24} />
          </div>
          <h1 className="mb-2 text-[22px] font-semibold text-[var(--color-text-strong)]">
            Check your inbox
          </h1>
          <p className="mb-6 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
            If an account exists for{" "}
            <span className="font-semibold text-[var(--color-text-body)]">
              {email.trim()}
            </span>
            , we&apos;ve sent a link to reset your password. The link expires in
            1 hour.
          </p>
          <div className="border-t border-[var(--color-border-subtle)] pt-4 text-center text-[12px]">
            <Link
              to="/login"
              className="font-semibold text-[var(--color-text-link)] hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 shadow-[var(--shadow-window)]">
        <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
          Reset your password
        </h1>
        <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
          Enter the email for your account and we&apos;ll send you a link to set
          a new password.
        </p>

        {formError !== null ? (
          <div className="mb-4">
            <AuthErrorBanner message={formError} />
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <AuthField
            label="Email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={submitting}
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send reset link"}
          </Button>
        </form>

        <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4 text-center text-[12px]">
          <Link
            to="/login"
            className="font-semibold text-[var(--color-text-link)] hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </main>
  );
}
